<template>
  <div data-component="base-search-input-wrapper">
    <input
      ref="input"
      data-input="search"
      :value="search"
      :placeholder="placeholder"
      @input="onSearchChange"
      @keydown.delete="onDeleteKeydown"
    >

    <base-search-options-popup
      v-if="isOptionsPopupOpen"
      :query="query"
      :schema="schema"
      :trigger="inputRef"
      :is-open="true"
      @query-change="onQueryChange"
    />

    <base-search-date-time-popup
      v-if="showDateTimePicker"
      :param="activeQueryParam"
      :trigger="inputRef"
      :is-open="true"
      @param-change="onParamChange"
      @close="onCloseDatepicker"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';

import BaseSearchOptionsPopup from './BaseSearchOptionsPopup.vue';
import BaseSearchDateTimePopup from './BaseSearchDateTimePopup.vue';
import { useSearch } from '../../composables';
import { QueryParam } from '../../types';
import { QUERY_PROP, SCHEMA_PROP, QUERY_PARAM_TYPE } from '../../constants';

export default defineComponent({
  name: 'BaseSearchInputWrapper',
  components: {
    BaseSearchOptionsPopup,
    BaseSearchDateTimePopup,
  },
  props: {
    search: {
      type: String,
      default: '',
    },
    query: QUERY_PROP,
    schema: SCHEMA_PROP,
    isInFocus: Boolean,
    isOptionsPopupOpen: Boolean,
  },
  emits: ['search-change'],
  setup(props) {
    const { schemaAsMap, activeQueryParam, isActiveQueryParamComplete } = useSearch(props);
    return { schemaAsMap, activeQueryParam, isActiveQueryParamComplete };
  },
  data() {
    return {
      inputRef: null as HTMLInputElement | null,
    };
  },
  computed: {
    placeholder() {
      return !this.query.length
        ? this.$t('HITL.common.search.search') as string
        : '';
    },

    inputType() {
      return this.showDateTimePicker ? 'datetime-local' : 'text';
    },

    showDateTimePicker() {
      if (!this.isOptionsPopupOpen) {
        return false;
      }

      if (!this.activeQueryParam || this.isActiveQueryParamComplete) {
        return false;
      }

      const schemaItem = this.schemaAsMap[this.activeQueryParam.key];
      const isParamDateRange = schemaItem.type === QUERY_PARAM_TYPE.DATE_RANGE;
      const isValuePartiallySet = !!this.activeQueryParam.value;

      return isParamDateRange && isValuePartiallySet;
    },
  },
  watch: {
    isInFocus(isInFocus, wasInFocus) {
      if (isInFocus !== wasInFocus) {
        isInFocus ? this.focus() : this.blur();
      }
    },
  },
  mounted() {
    this.inputRef = this.$refs['input'] as HTMLInputElement;
  },
  methods: {
    onSearchChange(event: KeyboardEvent) {
      const target = event.target as HTMLInputElement;
      this.$emit('search-change', target.value);
    },

    onDeleteKeydown() {
      if (this.search.length) {
        return true;
      } else {
        const query = this.query.slice(0, -1);
        this.$emit('query-change', query);
        this.focus();
      }
    },

    onQueryChange(query: QueryParam[]) {
      this.$emit('query-change', query);
      this.focus();
    },

    onParamChange(param: QueryParam) {
      const query = [...this.query.slice(0, -1), param];
      this.$emit('query-change', query);
      this.focus();
    },

    focus() {
      (this.$refs['input'] as HTMLInputElement).focus();
    },

    blur() {
      (this.$refs['input'] as HTMLInputElement).blur();
    },

    onCloseDatepicker(key: string) {
      const query = this.query.filter(q => q.key !== key);
      this.$emit('query-change', query);
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='base-search-input-wrapper'] {
  display: flex;
  position: relative;
  flex: 1 1 auto;
  /* Same height as a query param tag */
  height: 1.625rem;

  /* Input should grow with text length */
  [data-input='search'] {
    @include basic-input;
    width: fill-available;
    width: -moz-available;
    width: -webkit-fill-available;
    color: var(--or-c-on-background);
  }
}
</style>
